import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { doseDataState } from '~/state/atoms/dose/doseAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

export type ResolvedDoseTarget = {
    targetName: string;
    doseQuantity: number;
};

/**
 * Prescription targets of the loaded dose with each name resolved against
 * the live structure set, so renamed ROIs render under their current name.
 */
const useResolvedDoseTargets = (): ResolvedDoseTarget[] => {
    const doseData = useRecoilValue(doseDataState);
    const structureSet = useRecoilValue(currentStructureSetState);

    return useMemo(() => {
        if (!doseData?.targets) {
            return [];
        }
        const nameByRoiNumber = new Map<number, string>();
        for (const roi of structureSet?.roiArray ?? []) {
            nameByRoiNumber.set(roi.roiNumber, roi.name);
        }
        return doseData.targets
            .filter((target) => target.isTarget)
            .map((target) => ({
                targetName:
                    target.roiNumber !== undefined
                        ? (nameByRoiNumber.get(target.roiNumber) ?? target.name)
                        : target.name,
                doseQuantity: target.doseQuantity,
            }));
    }, [doseData?.targets, structureSet]);
};

export default useResolvedDoseTargets;
